import { inject, observer } from 'mobx-react';
import React from 'react';
import { Instance } from 'mobx-state-tree';
import { ActionCode, Page } from '../../common/enums';
import {
  AnchorStore,
  GroupStore,
  ApplicationStore,
  AuthenticationStore,
  BankHolidayDateStore,
  BlackPointStatusStore,
  BlackPointStore,
  DutyStore,
  LocaleStore,
  PharmacyDetailedStore,
  PharmacyStore,
  BlackPointTypeStore,
  SecretariatStore,
  DutyAssignmentStore,
  DutyOffRequestStore,
  UserStore,
  SystemStatusStore,
} from '../../store';
import withTranslate, { WithTranslateProps } from './WithTranslate';
import { DutyAssignment, BlackPoint, BankHolidayDate, DutyOffRequest, SystemStatus } from '../../store/models';
import PharmacyDutiesCalendar from '../Pharmacy/PharmacyDutiesCalendar';
import moment from 'moment';
import { MultiCalendarSelectionType } from './MultiCalendar';

interface SimplePharmacyCalendarProps extends WithTranslateProps {
  ApplicationStore?: Instance<typeof ApplicationStore>;
  AuthenticationStore?: Instance<typeof AuthenticationStore>;
  BankHolidayDateStore?: Instance<typeof BankHolidayDateStore>;
  BlackPointStatusStore?: Instance<typeof BlackPointStatusStore>;
  BlackPointTypeStore?: Instance<typeof BlackPointTypeStore>;
  BlackPointStore?: Instance<typeof BlackPointStore>;
  DutyAssignmentStore?: Instance<typeof DutyAssignmentStore>;
  DutyOffRequestStore?: Instance<typeof DutyOffRequestStore>;
  DutyStore?: Instance<typeof DutyStore>;
  LocaleStore?: Instance<typeof LocaleStore>;
  PharmacyDetailedStore?: Instance<typeof PharmacyDetailedStore>;
  PharmacyStore?: Instance<typeof PharmacyStore>;
  AnchorStore?: Instance<typeof AnchorStore>;
  GroupStore?: Instance<typeof GroupStore>;
  SystemStatusStore?: Instance<typeof SystemStatusStore>;
  UserStore?: Instance<typeof UserStore>;
  SecretariatStore?: Instance<typeof SecretariatStore>;

  pharmacyId: string;
  hideLegend?: boolean;
  synchronizedScrolling?: boolean;
  fly?: number;
  sly?: number;
  onCalendarMove?: (fly: number, sly: number) => void;
  onTypeChange?: (type: MultiCalendarSelectionType | undefined) => void;
  selectedType?: MultiCalendarSelectionType | undefined;
  separateToolbox?: boolean;

  inMultiCalendar?: boolean;
}

interface SimplePharmacyCalendarState {
  bankHolidayDates: Array<Instance<typeof BankHolidayDate>>;
  blackPointList: Array<Instance<typeof BlackPoint>>;
  dutyAssignments: Array<Instance<typeof DutyAssignment>>;
  dutyOffRequests: Array<Instance<typeof DutyOffRequest>>;
  systemStatusForSecretariat: Array<Instance<typeof SystemStatus>>;
  isLoading: boolean;
  pharmacyId: number;
  fly: number;
  sly: number;
  systemStatus?: any;
  selectedType?: MultiCalendarSelectionType;
}

@inject('ApplicationStore')
@inject('AuthenticationStore')
@inject('BankHolidayDateStore')
@inject('BlackPointStore')
@inject('BlackPointStatusStore')
@inject('BlackPointTypeStore')
@inject('DutyAssignmentStore')
@inject('DutyOffRequestStore')
@inject('DutyStore')
@inject('LocaleStore')
@inject('PharmacyDetailedStore')
@inject('PharmacyStore')
@inject('AnchorStore')
@inject('GroupStore')
@inject('UserStore')
@inject('SystemStatusStore')
@inject('SecretariatStore')
@observer
class SimplePharmacyCalendar extends React.Component<
  SimplePharmacyCalendarProps & WithTranslateProps,
  SimplePharmacyCalendarState
> {
  state = {
    bankHolidayDates: [],
    blackPointList: [],
    dutyAssignments: [],
    dutyOffRequests: [],
    systemStatusForSecretariat: [],
    isLoading: true,
    fly: Number(this.props.fly),
    sly: Number(this.props.sly),
    pharmacyId: Number(this.props.pharmacyId),
    systemStatus: null,
    selectedType: this.props.selectedType ? this.props.selectedType : undefined,
  };

  _isMounted = false;

  onTypeChange = (type: MultiCalendarSelectionType) => {
    if (this.state.selectedType !== type) {
      this.setState({ selectedType: type });
      if (this.props.onTypeChange) this.props.onTypeChange(type);
    }
  };

  async fetchData() {
    this.updateData(this.state.pharmacyId);
  }

  async updateData(newPharmacyId: number) {
    var pharmacyId: number = newPharmacyId;
    const { UserStore, AuthenticationStore, ApplicationStore, PharmacyStore } = this.props;

    await PharmacyStore!.loadListSafely(true, true);

    //load first by defaut
    //if (isNaN(pharmacyId) || pharmacyId <= 0) pharmacyId = PharmacyStore!.getFirst()!.pharmacyId;

    this._isMounted && this.setState({ isLoading: true, pharmacyId });

    let isPharmacy = false;
    if (
      AuthenticationStore!.userActionCodes.includes(ActionCode.PHARMACY_USER_CODE) &&
      pharmacyId !== AuthenticationStore!.pharmacyId
    ) {
      ApplicationStore!.navigate(Page.Unauthorized);
    } else if (AuthenticationStore!.userActionCodes.includes(ActionCode.PHARMACY_USER_CODE)) {
      isPharmacy = true;
    }

    const pharmacy = PharmacyStore!.getById(pharmacyId)!;
    const criteria: any = { role: ActionCode.PHARMACY_USER_CODE.toString() };
    await Promise.all([
      this.searchDuties({ pharmacyId }),
      this.searchBlackPoints({ pharmacyId }),
      this.loadDataForCalendar(pharmacyId, pharmacy?pharmacy.secretariatId:-1),
      //UserStore!.search(criteria),
    ]).then((responses) => {
      const nextAcceptableBlackpoint = this.state.systemStatus
        ? moment(this.state.systemStatus![0]['status'], 'DD/MM/YYYY').format('YYYY-MM-DD')
        : '';

      console.log('>>> setting state [' + pharmacyId + ']');

      //failsafe
      const blackpointNoInMobxStateTreeAnymore = responses[1].length != 0 && responses[1][0].type == undefined;
      if (!blackpointNoInMobxStateTreeAnymore) {
        this._isMounted &&
          this.setState({
            dutyAssignments: isPharmacy
              ? responses[0].filter((da) => da.duty.dutyDate.date <= nextAcceptableBlackpoint)
              : responses[0],
            blackPointList: responses[1],
            ...responses[2],
            isLoading: false,
            pharmacyId: pharmacyId,
            fly: Number(this.props.fly),
            sly: Number(this.props.sly),
          });
        console.log('>>> state successfully set [' + pharmacyId + ']');
      }
      console.log('>>> state not refreshed (blackpoint disconnected from mobx tree) [' + pharmacyId + ']');
    });
  }

  componentDidUpdate(prevProps: SimplePharmacyCalendarProps) {
    if (prevProps.pharmacyId !== this.props.pharmacyId || prevProps.fly !== this.props.fly) {
      this.fetchData();
    }
  }

  async componentDidMount() {
    this._isMounted = true;
    await this.props.AnchorStore!.loadListSafely(true, true);
    await this.props.GroupStore!.loadListSafely(true, true);
    const criteria: any = {
      subSystem: 'lastPublishedDuty',
    };
    const systemStatus = await this.props.SystemStatusStore!.search(criteria);
    const criteria2: any = { role: ActionCode.PHARMACY_USER_CODE.toString() };
    // await this.props.UserStore!.search(criteria2)
    this._isMounted && this.setState({ systemStatus });
    await this.fetchData();
  }

  componentWillUnmount() {
    this._isMounted = false;
  }

  searchBlackPoints = async (criteria: any) => {
    let result;
    await Promise.all([this.props.BlackPointStore!.search(criteria)])
      .catch((e) => console.log(e))
      .then((responses) => (result = responses[0]));

    return result;
  };

  searchDuties = async (criteria: any) => {
    const { DutyAssignmentStore, PharmacyStore, DutyStore } = this.props;
    let result;
    await Promise.all([
      DutyAssignmentStore!.searchAssignment(criteria),
      PharmacyStore!.searchForDutyAssignment(criteria),
      DutyStore!.searchForDutyAssignment(criteria),
    ])
      .catch(console.error)
      .then((responses) => (result = responses[0]));

    return result;
  };

  loadDataForCalendar = async (pharmacyId: number, secretariatId: number | undefined) => {
    let result = {};
    const promises = [
      this.props.BankHolidayDateStore!.loadListSafely(true, true),
      this.props.DutyOffRequestStore!.searchDutyOffRequest({ pharmacyId }),
    ] as Array<Promise<any>>;
    if (secretariatId != null) {
      const criteriaBP: any = { secretariatId };
      promises.push(this.props.SystemStatusStore!.search(criteriaBP));
    }

    await Promise.all(promises)
      .catch((e) => console.log(e))
      .then((responses) => {
        if (responses) {
          result = Object.assign(result, { bankHolidayDates: responses[0], dutyOffRequests: responses[1] });
          if (responses.length === 3) result = Object.assign(result, { systemStatusForSecretariat: responses[2] });
        }
      });

    return result;
  };

  saveCalendarChange = async (data: any) => {
    await this.props.PharmacyDetailedStore!.saveCalendarChange(data);
    this.searchBlackPoints({ pharmacyId: this.state.pharmacyId }).then(
      (resp) => this._isMounted && this.setState({ blackPointList: resp }),
    );
    this.searchDuties({ pharmacyId: this.state.pharmacyId }).then(
      (resp) => this._isMounted && this.setState({ dutyAssignments: resp }),
    );
  };

  onPharmacyChange = (selectedOption: any) => {
    this.updateData(Number(selectedOption.value));
  };

  onCalendarMove = (fly: number, sly: number) => {
    this.fetchData();
    if (this.props.onCalendarMove) this.props.onCalendarMove(fly, sly);
  };

  showPharmacy = (pharmacy: any) => {
    return (
      (this.props.AuthenticationStore!.isSuperAdmin && this.props.SecretariatStore!.selectedSecretariatId === 0) ||
      (this.props.AuthenticationStore!.isSuperAdmin &&
        pharmacy !== undefined &&
        this.props.SecretariatStore!.selectedSecretariatId !== undefined &&
        this.props.SecretariatStore!.selectedSecretariatId === pharmacy!.secretariatId) ||
      (pharmacy !== undefined &&
        this.props.AuthenticationStore!.isSecretariat &&
        this.props.AuthenticationStore!.secretariatId === pharmacy!.secretariatId)
    );
  };

  render() {
    const { PharmacyStore, hideLegend, synchronizedScrolling } = this.props;
    const { systemStatusForSecretariat, pharmacyId, blackPointList } = this.state;

    const pharmacyList = PharmacyStore!.dataList;
    const pharmacyDetails = PharmacyStore!.getByIdOrNew(pharmacyId);
    const blackpointNoInMobxStateTreeAnymore =
      blackPointList && blackPointList.length !== 0 && blackPointList[0]['type'] === undefined;

    var result;
    if (pharmacyId > 0 && pharmacyDetails && !blackpointNoInMobxStateTreeAnymore) {
      result = (
        <PharmacyDutiesCalendar
          rowCount={1}
          hideLegend={hideLegend}
          synchronizedScrolling={synchronizedScrolling}
          bankHolidayDateList={this.state.bankHolidayDates}
          blackPointList={this.state.blackPointList}
          dutyAssignments={this.state.dutyAssignments}
          dutyOffRequests={this.state.dutyOffRequests}
          pharmacyDetails={pharmacyDetails}
          saveCalendarChange={this.saveCalendarChange}
          pharmacyId={this.state.pharmacyId}
          fly={this.props.fly}
          sly={this.props.sly}
          onCalendarMove={this.onCalendarMove}
          systemStatus={systemStatusForSecretariat}
          isSecretariat={
            this.props.AuthenticationStore!.isSecretariat ||
            this.props.AuthenticationStore!.isAdmin ||
            this.props.AuthenticationStore!.isSuperAdmin
          }
          onTypeChange={this.onTypeChange}
          selectedType={this.props.selectedType}
          separateToolbox={this.props.separateToolbox}
          pharmacyList={pharmacyList.filter(this.showPharmacy)}
          onPharmacyChange={this.onPharmacyChange}
          inMultiCalendar={this.props.inMultiCalendar}
        />
      );
    } else {
      result = <div>Please wait...</div>;
    }

    return result;
  }
}

export default withTranslate(SimplePharmacyCalendar);
